import React, { useMemo, useState } from 'react';

import { AccountRangeHeader } from './AccountRangeHeader';
import { DashboardCard } from './DashboardCard';
import { MetricBarChart } from './MetricBarChart';
import { StatCardRadioGroup, type StatCardView } from './StatCardRadioGroup';
import { cadenceFromBuckets } from './cadenceFromBuckets';
import {
  METRIC_LENS,
  type MetricId,
  type MetricSeriesPoint,
  formatAvgRating,
  formatMetricValue,
  metricDisplayValue,
  metricIds,
  metricSubtitle,
  moneyMetricFor,
  snapshotCaption,
} from './metricLens';
import { ValueTag } from './primitives';
import { useDashboardBilling } from './useDashboardBilling';
import { useGroupStats } from './useDashboardData';
import { resolveRange, useDateRange } from './useDateRange';

// Account Overview orchestrator (ENG-5256 Phase 4B). Self-sources the date range,
// range-scoped group stats, and credit balance, then composes the header, the
// 8-card radiogroup, a single activity chart bound to the selected card, and
// range-filtered Value Delivered. Wired into DashboardBody in Phase 5.
//
// Data consistency: Avg Rating and Reviewed both read from the SAME
// useGroupStats(range) call — Avg Rating renders averageRating (null → "—",
// never a stale number) while Reviewed shows consultationsReviewed, so they can
// never disagree.

const PLAN_LABELS: Record<string, string> = {
  smb: 'Pro Subscription',
  enterprise: 'Enterprise Plan',
  advanced: 'Advanced Plan',
  basic: 'Basic Plan',
};

// ENG-5256 product decision: default selected card = Completed.
const DEFAULT_METRIC: MetricId = 'completed';

interface AccountOverviewCardProps {
  groupId: string | undefined;
  companyName: string;
  /** Raw plan key from group.capabilities.planType (e.g. "enterprise"); null hides the plan. */
  planType: string | null;
  groupSlug: string;
  isSMB: boolean;
  /**
   * Billing account id for the group. Credit teams read the credit balance from
   * it; SMB teams read the subscription plan amount. Undefined for non-billing
   * groups (skips the query).
   */
  billingAccountId: string | undefined;
}

function planLabelFor(planType: string | null): string | null {
  if (!planType) return null;
  return PLAN_LABELS[planType] ?? planType.charAt(0).toUpperCase() + planType.slice(1);
}

export function AccountOverviewCard({
  groupId,
  companyName,
  planType,
  groupSlug,
  isSMB,
  billingAccountId,
}: AccountOverviewCardProps): React.ReactNode {
  const { preset, setPreset } = useDateRange();
  const range = useMemo(() => resolveRange(preset), [preset]);
  const stats = useGroupStats(groupId, range);
  const { billing } = useDashboardBilling(billingAccountId);

  // The money slot is polymorphic: credit dashboards spend OFC credits
  // ("Credits Spent"), SMB dashboards spend USD ("Amount Spent"). Exactly one
  // occupies the first Activity card; everything downstream (card order, raw
  // values, chart series) derives from this single choice.
  const moneyMetric = moneyMetricFor(isSMB);
  const orderedIds = useMemo(() => metricIds(moneyMetric), [moneyMetric]);

  const [selectedId, setSelectedId] = useState<MetricId>(DEFAULT_METRIC);

  // The exact buckets feeding the chart for the selected metric. Each metric
  // reads from its own timeseries (Projects Created → projects, Expert Requests
  // → expert requests, everything else → consultations). Cadence AND chartData
  // both derive from this, so the x-axis labels can never describe a different
  // series than the bars actually drawn — which matters on the All-time preset,
  // where series can have different bucket origins/spacing.
  const chartBuckets = useMemo(() => {
    const { series } = METRIC_LENS[selectedId];
    if (series === 'expertRequests') return stats.expertRequestsTimeseries;
    if (series === 'projects') return stats.projectsCreatedTimeseries;
    return stats.consultationTimeseries;
  }, [
    selectedId,
    stats.consultationTimeseries,
    stats.expertRequestsTimeseries,
    stats.projectsCreatedTimeseries,
  ]);

  // Cadence read back from the actual spacing of the charted series' buckets,
  // falling back to the preset when there are too few buckets to measure.
  const cadence = useMemo(
    () =>
      cadenceFromBuckets(
        chartBuckets.map((b) => b.periodStart),
        preset
      ),
    [chartBuckets, preset]
  );

  // Live scalar backing each card, by metric id. Only the active money slot is
  // present (creditsSpentCents vs consultationSpendCents) — hence Partial.
  const rawValues = useMemo<Partial<Record<MetricId, number | null>>>(
    () => ({
      [moneyMetric]:
        moneyMetric === 'amount-spent' ? stats.consultationSpendCents : stats.creditsSpentCents,
      projects: stats.projectsCreated,
      'expert-requests': stats.expertRequests,
      'experts-engaged': stats.expertsEngaged,
      completed: stats.consultationsCompleted,
      reviewed: stats.consultationsReviewed,
      'pending-reviews': stats.consultationsPending,
      'avg-rating': stats.averageRating,
    }),
    [stats, moneyMetric]
  );

  const cards = useMemo<Record<MetricId, StatCardView>>(() => {
    const entries = orderedIds.map((id) => {
      // One pip→display conversion feeds the card value, the subtitle, AND the
      // chart series, so the three can never disagree.
      const display = metricDisplayValue(id, rawValues[id] ?? null);
      let value: string | null;
      let valueSuffix: string | undefined;
      if (id === 'avg-rating') {
        // Null avgRating → "—" (no star): never a stale number beside the fresh
        // Reviewed count. A real rating renders "4.2" with a ★ to its right.
        value = display === null ? null : formatAvgRating(display);
        valueSuffix = display === null ? undefined : '★';
      } else {
        // Amount Spent formats as dollars ("$1,250"); other metrics render bare.
        value = formatMetricValue(id, display);
      }
      return [id, { value, valueSuffix, subtitle: metricSubtitle(id, display, cadence) }] as const;
    });
    return Object.fromEntries(entries) as Record<MetricId, StatCardView>;
  }, [orderedIds, rawValues, cadence]);

  // Bind the chart to the selected card: read the SAME chartBuckets the cadence
  // is derived from via the lens accessor, then apply the SAME display transform
  // as the card value/subtitle (Credits chart converts pip → credits) so series,
  // cadence, card value, and subtitle all stay in agreement.
  const chartData = useMemo<MetricSeriesPoint[]>(() => {
    const config = METRIC_LENS[selectedId];
    return chartBuckets.map((bucket) => ({
      periodStart: bucket.periodStart,
      value: metricDisplayValue(selectedId, config.point(bucket)),
    }));
  }, [selectedId, chartBuckets]);

  const selectedConfig = METRIC_LENS[selectedId];
  const selectedSubtitle = cards[selectedId].subtitle;
  const isSnapshot = selectedConfig.lens === 'snapshot';
  const captionId = 'account-overview-snapshot-caption';

  const settingsUrl = `/team/${groupSlug}/settings`;
  const creditsRemaining = !isSMB && billing ? Math.round(billing.creditBalanceCents / 100) : null;

  return (
    <DashboardCard
      title="Account Overview"
      hideTitle
      loading={stats.loading && stats.consultationTimeseries.length === 0}
      error={stats.error}
      errorDetail={stats.errorDetail}
      onRetry={stats.refetch}
      fullWidth
    >
      <div className="flex flex-col gap-20">
        <AccountRangeHeader
          companyName={companyName}
          planLabel={planLabelFor(planType)}
          creditsRemaining={creditsRemaining}
          planPerSeatCents={isSMB ? (billing?.planPerSeatCents ?? null) : null}
          planPerSeatCurrency={isSMB ? (billing?.planPerSeatCurrency ?? null) : null}
          planQuantity={isSMB ? (billing?.planQuantity ?? null) : null}
          billingInterval={billing?.billingInterval ?? null}
          isSMB={isSMB}
          settingsUrl={settingsUrl}
          range={range}
          onPresetChange={setPreset}
        />

        <StatCardRadioGroup
          cards={cards}
          orderedIds={orderedIds}
          moneyMetric={moneyMetric}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />

        <div className="flex flex-col gap-4">
          <div aria-live="polite">
            <h3 className="text-14 font-semibold leading-snug text-on-surface">
              {selectedConfig.label}
            </h3>
            <p className="text-12 text-on-surface-muted">{selectedSubtitle}</p>
          </div>
          <div aria-describedby={isSnapshot ? captionId : undefined}>
            <MetricBarChart
              data={chartData}
              title={selectedConfig.label}
              unit={selectedConfig.unit}
              cadence={cadence}
              rating={selectedId === 'avg-rating'}
              decimals={selectedId === 'credits-spent' ? 1 : 0}
            />
          </div>
          {isSnapshot && (
            <p id={captionId} className="text-10 text-on-surface-subtle">
              {snapshotCaption(cadence)}
            </p>
          )}
        </div>

        <ValueDelivered
          reviewed={stats.consultationsReviewed}
          total={stats.consultationsCompleted}
          tags={stats.valueDelivered}
        />
      </div>
    </DashboardCard>
  );
}

interface ValueDeliveredProps {
  reviewed: number;
  total: number;
  tags: ReturnType<typeof useGroupStats>['valueDelivered'];
}

function ValueDelivered({ reviewed, total, tags }: ValueDeliveredProps): React.ReactNode {
  if (tags.length === 0) return null;

  return (
    <div className="border-t border-surface-border pt-14">
      <div className="mb-8 flex items-center gap-6 text-10 font-semibold uppercase tracking-wider text-on-surface-muted">
        <svg
          className="h-[1.4rem] w-[1.4rem] text-brand-primary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <span>Value Delivered</span>
        <span className="font-normal normal-case tracking-normal text-on-surface-muted">
          &middot; from {reviewed} of {total} reviewed
        </span>
      </div>
      <div className="flex flex-wrap gap-6">
        {tags.map((tag) => (
          <ValueTag key={tag.type} type={tag.type} count={tag.count} />
        ))}
      </div>
    </div>
  );
}
